import { usePatchLandelijkeFaciliteiten, usePostLandelijkeFaciliteiten, VestigingStatus } from "@/entities/vestiging";
import { Button, Checkboxfield, Separator } from "@/shared/components";
import { SetStateAction, useState } from "react";

type AanvraagButtonProps = {
    dossierId: string;
    status: VestigingStatus | undefined;
};

export function AanvraagAction({ dossierId, status }: AanvraagButtonProps) {
    const { mutate: postLandelijkeFaciliteiten } = usePostLandelijkeFaciliteiten();
    const { mutate: patchLandelijkeFaciliteiten } = usePatchLandelijkeFaciliteiten();
    const [conditionChecked, setConditionChecked] = useState(false);
    const [submitActionError, setSubmitActionError] = useState<string>();

    const setConditionCheckedWrapper = (value: SetStateAction<boolean>) => {
        setSubmitActionError(undefined);
        setConditionChecked(value);
    };

    const aanvraagHandler = () => {
        const needsConditionChecked = status === undefined || status === "Gestopt" || status === "GaatNietLeveren";
        if (needsConditionChecked && !conditionChecked) {
            setSubmitActionError(CheckboxRequirement.message);
            return;
        }
        const newStatus = status == "Actief" ? VestigingStatus.STOP_AANGEVRAAGD : VestigingStatus.BESTELD;
        if (newStatus === "Besteld") {
            postLandelijkeFaciliteiten({ dossierId });
        } else {
            patchLandelijkeFaciliteiten({ dossierId, status: newStatus });
        }
    };

    if (!status) {
        // No aanvraag yet
        return (
            <div className="flex flex-col gap-4">
                <Separator orientation="horizontal" />
                <CheckboxRequirement
                    conditionChecked={conditionChecked}
                    setConditionChecked={setConditionCheckedWrapper}
                    submitActionError={submitActionError}
                />
                <Button
                    className="w-min text-nowrap"
                    variant="primary"
                    icon="send"
                    iconStrokeWidth="thick"
                    data-testid="aanvraag-action-button"
                    onClick={aanvraagHandler}
                >
                    Aanvraag landelijke faciliteiten versturen
                </Button>
            </div>
        );
    }

    switch (status) {
        case "Besteld":
        case "StopAangevraagd":
            return null;
        case "Actief":
            return (
                <div className="flex flex-col gap-4">
                    <Separator orientation="horizontal" />
                    <Button
                        className="w-min text-nowrap"
                        variant="secondary"
                        icon="stop"
                        iconStrokeWidth="thick"
                        data-testid="aanvraag-action-button"
                        onClick={aanvraagHandler}
                    >
                        Landelijke faciliteiten stopzetten
                    </Button>
                </div>
            );
        case "GaatNietLeveren":
        case "Gestopt":
            return (
                <div className="flex flex-col gap-4">
                    <Separator orientation="horizontal" />
                    <CheckboxRequirement
                        conditionChecked={conditionChecked}
                        setConditionChecked={setConditionCheckedWrapper}
                        submitActionError={submitActionError}
                    />
                    <Button
                        className="w-min text-nowrap"
                        variant="secondary"
                        icon="send"
                        iconStrokeWidth="thick"
                        data-testid="aanvraag-action-button"
                        onClick={aanvraagHandler}
                    >
                        Landelijke faciliteiten aanvragen
                    </Button>
                </div>
            );
    }
}

type CheckboxRequirementProps = {
    conditionChecked: boolean;
    setConditionChecked: (conditionChecked: boolean) => void;
    submitActionError: string | undefined;
};

function CheckboxRequirement({ conditionChecked, setConditionChecked, submitActionError }: CheckboxRequirementProps) {
    return (
        <Checkboxfield
            id="landelijke-faciliteiten-condition"
            className="col-span-2 gap-x-2"
            labelClassName="text-m font-normal"
            label="Ja, ik wil alle vestigingen van Kiosk en StationsHuiskamer openstellen"
            checked={conditionChecked}
            onCheckedChange={setConditionChecked}
            errorMessage={submitActionError}
        />
    );
}
CheckboxRequirement.message = "Vink bovenstaande aan voordat je de aanvraag verstuurt";
